import { lazy, Suspense, useMemo, useContext } from "react"
import { Loader } from "widgets"
import PrivateRoute from "./private-route.js"
import RouteError from "./route-error.js"
import { useAgencyRoutes } from "../agency/agency-routes.js"
import { useAccountsRoutes } from "../accounts/accounts-routes.js"
import { useUserRoutes } from "../user/user-routes.js"
import { AppContext } from "./app-context-provider.js"
import { featuresFlatMap } from "utils"
import { useVisaRoutes } from "../visa/visa-routes.js"
import { useHelpDeskRoutes } from "../help-desk/help-desk-routes.js"

const Login = lazy(() => import("../user/login.js"))
const ForgotPassword = lazy(() => import("../user/forgot-password.js"))
const VisaeroLogin = lazy(() => import("../visaero/login.js"))
const AgencyRegistration = lazy(() => import("../agency/registration.js"))

export const useAppRoutes = () => {
  const appContext = useContext(AppContext)

  const features = useMemo(
    () => featuresFlatMap(appContext?.user?.featureDTOList),
    [appContext?.user?.featureDTOList],
  )

  const agencyRoutes = useAgencyRoutes()
  const userRoutes = useUserRoutes()
  const accountsRoutes = useAccountsRoutes()
  const visaRoutes = useVisaRoutes()
  const helpDeskRoutes = useHelpDeskRoutes()

  const routes = useMemo(
    () =>
      _.compact([
        {
          path: "/",
          errorElement: <RouteError />,
          element: <PrivateRoute />,
          children: _.compact([
            features["home"]?.dashboard && {
              index: true,
              element: (
                <Suspense fallback={<Loader />}>
                  <VisaeroLogin />
                </Suspense>
              ),
            },
            ...agencyRoutes,
            ...visaRoutes,
            ...helpDeskRoutes,
            ...userRoutes,
            ...accountsRoutes,
          ]),
        },
        {
          path: "/login",
          element: (
            <Suspense fallback={<Loader />}>
              <Login />
            </Suspense>
          ),
        },
        {
          path: "/forgot-password",
          element: (
            <Suspense fallback={<Loader />}>
              <ForgotPassword />
            </Suspense>
          ),
        },
        {
          path: "/register-agency",
          element: (
            <Suspense fallback={<Loader />}>
              <AgencyRegistration />
            </Suspense>
          ),
        },
      ]),
    [
      accountsRoutes,
      agencyRoutes,
      features,
      helpDeskRoutes,
      userRoutes,
      visaRoutes,
    ],
  )

  return routes
}
